import { saveLocalStorage } from 'configs/localStorage';

import GA from 'constants/ga';
import ReactGA from 'react-ga';

import { isAuth } from 'store/slices/authorizations';
import { changeUserData } from 'store/slices/profile';
import { message } from 'style-guide/ToastMessages';
import nestAPi from '../nestApi';

const userAuth = nestAPi.injectEndpoints({
  endpoints: (build) => ({
    createUser: build.mutation({
      query: (params) => ({
        url: 'auth/signup',
        method: 'POST',
        body: params,
      }),
    }),
    userLogin: build.mutation({
      query: (params) => ({
        url: 'auth/login',
        method: 'POST',
        body: params,
      }),
      invalidatesTags: ['getPopularCourses'],
      transformResponse: (response, _meta, params) => {
        window.pendo.initialize({
          visitor: {
            id: response.id,
          },
          account: {
            id: response.domainId,
          },
        });
        if (response.adminUser === true) {
          saveLocalStorage({
            adminUser: response,
          });
          return {
            adminUser: response,
          };
        }
        ReactGA.event({
          category: GA.categories.users,
          action: GA.actions.signIn,
          label: params?.domainKey,
        });
        saveLocalStorage({
          user: response,
        });
        return {
          user: response,
        };
      },

      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        if (data.user?.accessToken || data.adminUser?.accessToken) {
          dispatch(isAuth({ isAuth: true, userData: data }));
          dispatch(changeUserData({ isAuth: true, userData: data.user }));
        }
      },
    }),
    verifyUserEmail: build.mutation({
      query: (params) => ({
        url: `auth/verify/${params.token}`,
        method: 'GET',
        params,
      }),
      transformErrorResponse: () => {
        message.error(
          'To verify your email address, please follow this single-use link: Finish your registration and Sign In'
        );
      },
    }),
    sendResetPassword: build.mutation({
      query: (params) => ({
        url: 'auth/forgot-password',
        method: 'POST',
        body: params,
      }),

      transformResponse: (_response, _meta, params) => {
        ReactGA.event({
          category: GA.categories.users,
          action: GA.actions.forgotPassword,
          label: params?.domainKey,
        });
      },
    }),
    resetPassword: build.mutation({
      query: (params) => ({
        url: `auth/reset-password/${params.token}`,
        method: 'POST',
        body: params,
      }),
    }),
    logOut: build.mutation({
      query: (params) => ({
        url: 'auth/logout',
        method: 'POST',
        params,
      }),
    }),
    resendTwoFa: build.mutation({
      query: (params) => ({
        url: 'auth/resend2FA',
        method: 'POST',
        body: params,
      }),
    }),
    updateUserNumber: build.mutation({
      query: (params) => ({
        url: 'auth/phoneNumber',
        method: 'POST',
        body: params,
      }),
    }),
    verifyTwoFa: build.mutation({
      query: (params) => ({
        url: 'auth/verify-2fa',
        method: 'POST',
        body: params,
      }),
      transformResponse: (response, _meta, params) => {
        window.pendo.initialize({
          visitor: {
            id: response.id,
          },
          account: {
            id: response.domainId,
          },
        });
        if (response.adminUser === true) {
          saveLocalStorage({
            adminUser: response,
          });
          return {
            adminUser: response,
          };
        }
        ReactGA.event({
          category: GA.categories.users,
          action: GA.actions.signIn,
          label: params?.domainKey,
        });
        saveLocalStorage({
          user: response,
        });
        return {
          user: response,
        };
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        if (data.user?.accessToken || data.adminUser?.accessToken) {
          dispatch(isAuth({ isAuth: true, userData: data }));
          dispatch(changeUserData({ isAuth: true, userData: data.user }));
        }
      },
    }),
  }),
});

export const {
  useCreateUserMutation,
  useUserLoginMutation,
  useVerifyUserEmailMutation,
  useSendResetPasswordMutation,
  useResetPasswordMutation,
  useLogOutMutation,
  useResendTwoFaMutation,
  useUpdateUserNumberMutation,
  useVerifyTwoFaMutation,
} = userAuth;

export default userAuth;
